// YouTube-style "Auto" quality policy for the progressive tier system
// (useProgressiveTierSrc and friends). Mirrors the signals YouTube's auto
// setting weighs when picking a rendition, adapted to this app's discrete
// placeholder→small(~960px)→large(~1600px)→original ladder:
//
//   1. Measured throughput — a rolling harmonic mean of recently observed
//      tier downloads (harmonic, like DASH players use, so one lucky burst
//      can't inflate the estimate), seeded from the browser's own
//      navigator.connection.downlink until a real sample exists. An upgrade
//      is only attempted when the estimate comfortably exceeds what the next
//      tier needs (a switch-up safety factor, so quality never flaps on a
//      borderline connection).
//   2. Player size — never fetch pixels the element can't show (CSS width ×
//      devicePixelRatio, DPR capped at 2 the way image CDNs cap it).
//      YouTube's auto never streams 4K into a 480px player.
//   3. User intent — Save-Data or a 2g/3g effective connection type caps
//      quality outright.
//
// Decisions are made at upgrade moments (right after the site-wide
// small-tier wave settles — see progressiveTierGate.ts), not continuously:
// these are stills and short loops, not 2-hour streams, and a tier below the
// resolved one is never re-fetched, so there is no downshift path.
//
// No decision here ever blocks a BASELINE load: the small tier (or original
// when it's the only source, or any fallback after a broken tier) always
// loads exactly as before — the policy only vetoes OPTIONAL upgrades, so the
// worst case is the pre-ABR behavior, never a blank element.

/** 1 = small (~960px), 2 = large (~1600px), 3 = original. */
type TierRank = 1 | 2 | 3;

const SMALL_TIER_PX = 960;
const LARGE_TIER_PX = 1600;
const VIDEO_SMALL_TIER_PX = 640;
// A display box within 10% of a tier's native width doesn't benefit from the
// next tier up — avoids upgrading a whole page because a box is 970px wide.
const DISPLAY_SLACK = 1.1;

const MAX_SAMPLES = 8;
// Latency-dominated (tiny) or cache-served (instant) transfers say nothing
// about link throughput — only count real, meaningfully-sized downloads.
const MIN_SAMPLE_BYTES = 24 * 1024;
const MIN_SAMPLE_MS = 20;
// Only switch up when the estimate beats the requirement by this factor —
// the conservative up-switch margin ABR players use to avoid stalls.
const UP_SWITCH_SAFETY = 1.4;
// Coarse per-tier affordability floors (Mbps) for when the next tier's byte
// size isn't known client-side (the public site render path exposes only
// variant URLs, never sizes).
const LARGE_MIN_MBPS = 1.5;
const ORIGINAL_MIN_MBPS = 5;
const VIDEO_ORIGINAL_MIN_MBPS = 5;

const samplesMbps: number[] = [];

interface NetworkInformationLike {
  downlink?: number;
  effectiveType?: string;
  saveData?: boolean;
}

function connectionInfo(): NetworkInformationLike | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as { connection?: NetworkInformationLike }).connection ?? null;
}

/** Feed the estimator one observed transfer (e.g. a fetch you timed yourself). */
export function recordTransferThroughput(bytes: number, ms: number): void {
  if (!(bytes >= MIN_SAMPLE_BYTES) || !(ms >= MIN_SAMPLE_MS)) return;
  samplesMbps.push((bytes * 8) / (ms * 1000));
  if (samplesMbps.length > MAX_SAMPLES) samplesMbps.shift();
}

/**
 * Feed the estimator from the Resource Timing entry of a just-completed URL
 * (the tier preloads use this — no way to time an Image() fetch directly).
 * transferSize is 0 for HTTP-cache hits and for cross-origin responses
 * without Timing-Allow-Origin; both are useless as throughput evidence and
 * are skipped by the size floor, so the estimator degrades to unseeded
 * rather than skewed.
 */
export function recordResourceThroughput(url: string): void {
  try {
    const entries = performance.getEntriesByName(url, 'resource') as PerformanceResourceTiming[];
    const e = entries[entries.length - 1];
    if (!e) return;
    recordTransferThroughput(e.transferSize, e.responseEnd - (e.requestStart || e.startTime));
  } catch {
    /* Performance API missing/blocked — estimator just stays unseeded */
  }
}

/** Harmonic mean of recent samples; navigator.connection.downlink until one
 *  exists; null when the browser offers neither (Safari). */
export function estimatedMbps(): number | null {
  if (samplesMbps.length > 0) {
    let inv = 0;
    for (const s of samplesMbps) inv += 1 / s;
    return samplesMbps.length / inv;
  }
  const dl = connectionInfo()?.downlink;
  return typeof dl === 'number' && dl > 0 ? dl : null;
}

function effectiveTypeRank(): TierRank {
  const et = connectionInfo()?.effectiveType;
  if (et === 'slow-2g' || et === '2g') return 1;
  if (et === '3g') return 2;
  return 3;
}

/** The highest tier the network evidence supports right now. Unknown
 *  bandwidth (no samples, no connection API) ranks 3 — no evidence is not a
 *  reason to withhold quality, it's the pre-ABR status quo. */
function networkRank(): TierRank {
  if (connectionInfo()?.saveData === true) return 1;
  const est = estimatedMbps();
  const estRank: TierRank = est == null ? 3 : est < LARGE_MIN_MBPS ? 1 : est < ORIGINAL_MIN_MBPS ? 2 : 3;
  return Math.min(effectiveTypeRank(), estRank) as TierRank;
}

/** The highest tier this element's display box can actually show. No hint =
 *  no display cap (rank 3). For full-bleed `background-size: cover`
 *  surfaces, pass the LARGER viewport axis — cover scales to the limiting
 *  dimension, so a portrait phone still needs a tall-enough source. */
function displayRank(displayCssPx: number | null | undefined): TierRank {
  if (!displayCssPx || displayCssPx <= 0) return 3;
  const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
  const needed = displayCssPx * dpr;
  if (needed <= SMALL_TIER_PX * DISPLAY_SLACK) return 1;
  if (needed <= LARGE_TIER_PX * DISPLAY_SLACK) return 2;
  return 3;
}

/** Display-size hint for full-bleed cover backgrounds (hero/section). */
export function coverViewportPx(): number {
  if (typeof window === 'undefined') return 1920;
  return Math.max(window.innerWidth, window.innerHeight);
}

export interface ImageUpgradePlan {
  /** URL to stage after the small tier, or null to stay on small. */
  midTier: string | null;
  /** Whether the opt-in finalTier:'original' stage-3 pass may run. */
  allowFinalOriginal: boolean;
}

/**
 * Decide the upgrade ladder for one image whose small tier has already
 * loaded successfully. `midTier` mirrors nextTierAfterSmall's shape (the URL
 * to stage next) so the hook can drop it in unchanged; when the cap is
 * 'large' but no large tier exists, the nearest tier that satisfies the
 * display is the original — substituted only when the NETWORK also supports
 * rank 3 (an original can be 10×+ a large's bytes; a display-only cap is not
 * license to fetch it on a link that can't afford it).
 */
export function planImageUpgrade(opts: {
  large?: string | null;
  original: string;
  displayCssPx?: number | null;
}): ImageUpgradePlan {
  const net = networkRank();
  const cap = Math.min(net, displayRank(opts.displayCssPx)) as TierRank;
  let midTier: string | null;
  if (cap <= 1) midTier = null;
  else if (cap === 2) midTier = opts.large || (net === 3 ? opts.original : null);
  else midTier = opts.large || opts.original;
  return { midTier, allowFinalOriginal: cap === 3 };
}

/**
 * Whether a video that is already playing its small (~640px) tier should
 * fetch the original. When the caller knows the original's byte size and
 * duration (the owner-authenticated library popout does), the check is
 * YouTube-exact: measured throughput must beat the original's real average
 * bitrate by the up-switch safety factor. Public-site call sites know
 * neither, so they fall back to a coarse Mbps floor. Unknown bandwidth
 * (Safari, no samples yet) allows the upgrade — pre-ABR status quo; the
 * gallery hook additionally never risks a stall because it swaps only after
 * a complete background download.
 */
export function planVideoUpgrade(opts: {
  displayCssPx?: number | null;
  originalBytes?: number | null;
  durationSeconds?: number | null;
}): boolean {
  const conn = connectionInfo();
  if (conn?.saveData === true) return false;
  // An original-quality video is never a safe fetch on a 2g/3g link.
  if (effectiveTypeRank() < 3) return false;
  if (opts.displayCssPx && opts.displayCssPx > 0) {
    const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
    if (opts.displayCssPx * dpr <= VIDEO_SMALL_TIER_PX * DISPLAY_SLACK) return false;
  }
  const est = estimatedMbps();
  if (est == null) return true;
  const requiredMbps = opts.originalBytes && opts.durationSeconds && opts.durationSeconds > 0
    ? (opts.originalBytes * 8) / (opts.durationSeconds * 1e6)
    : null;
  return requiredMbps != null
    ? est >= requiredMbps * UP_SWITCH_SAFETY
    : est >= VIDEO_ORIGINAL_MIN_MBPS;
}
